import React, { useRef, useState, useEffect, useMemo, Dispatch, useContext, createContext, CSSProperties, ReactElement } from 'react'
import { X, Y, Z, e, Vec2, Vec3, proj, unproj, mul, div, add, sub, normTo1, random3 } from './VectorTools'

import { Segment, distancePoints, distancePointLine, inSegmentRange, rotate2dVec, projOn2DLine, segmentOfPoints, intersectSegments, tup2 } from './GeometryTools'

import RabbitSad from './images/rabbit_sad_simple.svg'
import RabbitHappy from './images/rabbit.svg'

import BearSad from './images/teddy_sad_simple.svg'
import BearHappy from './images/teddy_simple.svg'

import FoxSad from './images/foxxy_sad.svg'
import FoxHappy from './images/foxxy_simple.svg'

import Soup from './images/soup.svg'

import Leak from './images/leak.svg'
import Onion from './images/onion.svg'
import Carrot from './images/carrot.svg'

import LeakCropped from './images/crop/leak.svg'
import OnionCropped from './images/crop/onion.svg'
import CarrotCropped from './images/crop/carrot.svg'

import LeakCroppedBW from './images/crop/leak-bw.svg'
import OnionCroppedBW from './images/crop/onion-bw.svg'
import CarrotCroppedBW from './images/crop/carrot-bw.svg'

let vecsToPath = (...vecs: Vec2[]) => 'M ' + vecs.map(([a, b]) => `${a} ${b}`).join(' L ');

let CenteredImage = ({ pos, img, scale, rotate, style }: { pos: Vec2, img: string, scale?: number, rotate?: number, style?: CSSProperties }) =>
    <g transform={`translate(${pos[0]}, ${pos[1]})`} style={style} >
        <g className="g-neutral">
            <g transform={`scale(${scale || 0.1})`} >
                <g className="image-rotation-g" style={{
                    transformBox: 'fill-box',
                    transformOrigin: 'center',
                    transform: `rotate(${rotate}deg)`,
                }}>
                    <image x="0" y="0" height="2" width="2"
                        transform={`translate(-1,-1)`} href={img} />
                </g>
            </g>
        </g>
    </g>;

export type Obj = {
    movable: boolean,
    vec: Vec3;
    image: string,
    alt_image?: string,
    id: number,
    kind: 'soup' | 'client',
    className?: string,
    displayArrows: [boolean, boolean, boolean]
}

let mkId: () => number = (() => {
    let id = 0;
    return () => id++;
})();
export type MkObjT = { position?: Vec3, className?: string, movable?: boolean, displayArrows?: [boolean, boolean, boolean] };
export let MkSoup = ({ position, className, movable, displayArrows }: MkObjT): Obj => ({
    movable: movable === undefined ? true : movable,
    className: className || '',
    vec: position ? normTo1(position) : random3(),
    kind: 'soup',
    id: mkId(),
    image: Soup,
    displayArrows: displayArrows || [false, false, false]
});
let images = {
    rabbit: tup2(RabbitHappy, RabbitSad),
    fox: tup2(FoxHappy, FoxSad),
    bear: tup2(BearHappy, BearSad),
};
export let MkClient = (who: keyof typeof images, d: MkObjT, image = images[who][0], alt_image = images[who][1]): Obj => (Object.assign(MkSoup(d), {
    kind: 'client',
    className: ((d.className || '') + ' is-a-' + who).replace(/ +/, ' '),
    image, alt_image
}));

type ST<T> = [T, Dispatch<T>]

let f = { X: proj(X), Y: proj(Y), Z: proj(Z), e: proj(e) }
let lines = [[f.X, f.Y], [f.Y, f.Z], [f.Z, f.X]].map(([A, B]) => segmentOfPoints(A, B));

export let listIngredients: { name: string, imgs: { normal: string, cropped: string, croppedBW: string }, axis: string, vec: Vec2, rotate: number, d: number }[] = [
    { name: 'onion', imgs: { normal: Onion, cropped: OnionCropped, croppedBW: OnionCroppedBW }, axis: 'X', vec: f.X, rotate: 0, d: 0.02 },
    { name: 'carrot', imgs: { normal: Carrot, cropped: CarrotCropped, croppedBW: CarrotCroppedBW }, axis: 'Y', vec: f.Y, rotate: 248, d: 0 },
    { name: 'leak', imgs: { normal: Leak, cropped: LeakCropped, croppedBW: LeakCroppedBW }, axis: 'Z', vec: f.Z, rotate: 0, d: 0 },
]

let enforcePosition = (v: Vec2): Vec2 => {
    let intersects = lines.map(s => tup2(s, intersectSegments(s, segmentOfPoints(f.e, v)))).filter(
        (x: [Segment, Vec2 | null]): x is [Segment, Vec2] => x[1] !== null
    );
    let len = intersects.length;
    if (len >= 3)
        throw new Error("enforcePosition: 3 way intersection should be impossible");
    if (len === 0)
        return v;
    if (len === 1)
        return intersects[0][1];
    let i = intersectSegments(intersects[0][0], intersects[1][0]);
    if (i === null)
        throw new Error("enforcePosition: final intersection impossible");
    return i;
}

function isObj<T>(x: T | Obj): x is Obj { return x instanceof Object && 'vec' in x; };

export type Options = {
    compositeSoups: boolean,
    delta: number,
    delta_visual?: number,
    object_scale: number,
    objects: ST<Obj>[],
    near_clients_rotate: boolean
    show_soup_areas?: boolean,
};

function Arrow({ a, b, text, textAngle, dL, dR, hideArrow, className, marker }: { a: Vec2, b: Vec2, text: String, textAngle: number, dL?: number, dR?: number, hideArrow: boolean, marker: string, className?: string }) {
    let angle = Math.atan2(a[1] - b[1], a[0] - b[0]);
    let ii = rotate2dVec([1, 0], angle);
    let iL = mul(ii, dL || 0.08);
    let iR = mul(ii, dR || 0.08);
    let m = div(add(a, b), 2);
    let mm = add(m, mul(ii, 0.03));
    let left = sub(m, mul(ii, 0.045));
    let right = add(m, mul(ii, 0.042));
    if (textAngle == 0 || textAngle == 180) {
        if (textAngle == 0)
            mm = add(m, mul(rotate2dVec([1, 0], angle + Math.PI * 0.5), 0.03));
        else
            mm = sub(m, mul(rotate2dVec([1, 0], angle + Math.PI * 0.5), 0.03));
        left = sub(m, mul(ii, (dR || 0.08) - 0.024));
        right = add(m, mul(ii, (dL || 0.08) + 0.016));
    }
    return (<g className={`arrow ${className || ''}`} >
        {hideArrow ||
            <>
                <path strokeWidth='2px' stroke='#f0d59d' d={vecsToPath(left, add(b, iL))} markerEnd={`url(#${marker})`} />
                <path strokeWidth='2px' stroke='#f0d59d' d={vecsToPath(right, sub(a, iR))} markerEnd={`url(#${marker})`} />
            </>
        }
        {/*         <path strokeWidth='2px' stroke='#f0d59d' d={vecsToPath(sub(a, i), add(b, i))} marker-end="url(#arrowhead)" marker-start="url(#arrowhead-end)" /> */}
        <text textAnchor="middle" style={{ fontSize: '0.1px' }} transform={`translate(${mm[0]}, ${mm[1]}) rotate(${(angle * 180) / Math.PI + textAngle})`} >
            {text}
        </text >
    </g >);
}

export function Visualization2D(props: Options) {
    let objects = props.objects;
    let vdelta = props.delta_visual || props.delta;

    let soups = useMemo(() => objects.filter(([{ kind }]) => kind === "soup"), [objects]);
    let clients = useMemo(() => objects.filter(([{ kind }]) => kind === "client"), [objects]);
    let show_soup_areas: boolean = props.show_soup_areas === undefined ? clients.length > 0 : props.show_soup_areas
    let composite = useMemo<null | { s: Segment, a: Vec2, b: Vec2, center: Vec2, angle: number, aVec: Vec2 }>(() => {
        if (!props.compositeSoups)
            return null;
        if (soups.length !== 2)
            throw new Error("Configured with `compositeSoups` option without having exactly 2 soups.");
        let [a, b] = soups.map(([s]) => proj(s.vec));
        let angle = Math.atan2(a[1] - b[1], a[0] - b[0]);
        return {
            s: segmentOfPoints(a, b), a, b,
            angle,
            center: div(add(a, b), 2),
            aVec: rotate2dVec([vdelta, 0], angle + Math.PI / 2)
        };
    }, [soups, props]);

    let connexions: [Obj, Obj][] = [];
    type clientStatesT = [Obj, Vec2, Vec2, boolean];
    let clientStates: Map<number, boolean | clientStatesT | Obj> = new Map();
    if (composite) {
        for (let [c] of clients) {
            let o = proj(c.vec);
            let proj_on_line = projOn2DLine(o, composite.s.line);
            let inRect = inSegmentRange(proj_on_line[0], composite.s) && distancePointLine(o, composite.s.line) <= props.delta;
            let inCircleA = distancePoints(o, composite.a) <= props.delta;
            let inCircleB = distancePoints(o, composite.b) <= props.delta;
            let inCircle = inCircleA || inCircleB;
            let inArea = inRect || inCircleA || inCircle;
            let nearestCircle: Vec2 = inCircleA ? composite.a : composite.b;
            /*             let distance_to_line = distancePoints(proj_on_line, o); */
            /*             let distance_to_near_soup = fP ? distancePoints(fP, o) : Infinity; */
            clientStates.set(c.id, inArea ? [
                c, o, inRect ? proj_on_line : nearestCircle,
                !inRect
            ] : false);
        }
    } else
        clients.forEach(([c]) => soups.forEach(([s]) =>
            distancePoints(s.vec, c.vec) < props.delta && (clientStates.set(c.id, s), connexions.push([s, c]))
        ))

    let [active, setActive] = useState<null | {
        obj: [Obj, Dispatch<Obj>],
        initialVec: Vec2,
        initialMouse: Vec2
    }>(null);
    let updateActivePosition = (vec: Vec2) => {
        if (!active)
            return;
        let obj = active.obj[0];
        active.obj[1]({ ...obj, vec: unproj(enforcePosition(vec)) });
        setOrders([...orders.filter(id => obj.id !== id), obj.id]);
    };
    let [currentMouse, setCurrentMouse] = useState<null | Vec2>(null);

    let svg_el = useRef<SVGSVGElement | null>(null);
    let getCoord = (e: React.PointerEvent): Vec2 => {
        if (!svg_el.current)
            throw new Error("Missing ParentSVGContext?");
        let point = svg_el.current.createSVGPoint();
        point.x = e.clientX;
        point.y = e.clientY;
        point = point.matrixTransform((svg_el.current.getScreenCTM() as DOMMatrix).inverse());
        return [point.x, point.y];
    };
    let move = (e: React.PointerEvent) => {
        if (!active || !currentMouse)
            return;
        let cur = getCoord(e);
        setCurrentMouse(cur);
        let δ = sub(cur, active.initialMouse);
        updateActivePosition(add(active.initialVec, δ));
    }
    let up = (e: React.PointerEvent) => {
        setActive(null);
        setCurrentMouse(null);
    };

    let [orders, setOrders] = useState<number[]>([]);
    let sorted = useMemo(() => [...objects].sort(([a], [b]) => orders.indexOf(a.id) - orders.indexOf(b.id)), [objects, orders]);
    let s = ([a, b]: Vec2) => a + ' ' + b;
    return (<>
        <svg className="widget-2d" onPointerMove={move} ref={svg_el} onPointerUp={up} width="600" height="500" viewBox="-0.9 -1.04 1.8 1.6">
            <defs>
                <marker id="arrowhead-X" markerWidth="8" markerHeight="6"
                    refX="0" refY="3.5" orient="auto">
                    <polygon className='arrowhead X' points="0 0, 5 3.5, 0 7" />
                </marker>
                <marker id="arrowhead-Y" markerWidth="8" markerHeight="6"
                    refX="0" refY="3.5" orient="auto">
                    <polygon className='arrowhead Y' points="0 0, 5 3.5, 0 7" />
                </marker>
                <marker id="arrowhead-Z" markerWidth="8" markerHeight="6"
                    refX="0" refY="3.5" orient="auto">
                    <polygon className='arrowhead Z' points="0 0, 5 3.5, 0 7" />
                </marker>
            </defs>

            <g className='axes'>
                {listIngredients.map(({ vec, imgs, rotate, axis, d }, i) =>
                    <g key={i} className={`axis ${axis}`}>
                        <path d={vecsToPath(f.e, mul(vec, 1.07))} />
                        <CenteredImage pos={mul(vec, 1.15 + d)} img={imgs.normal} rotate={rotate} />
                    </g>
                )}
            </g>
            <path className="triangle" d={vecsToPath(f.X, f.Y, f.Z) + ' Z'} />
            <g>
                {
                    composite ?
                        <g>
                            <path d={`M ${s(add(composite.b, composite.aVec))} L ${s(add(composite.a, composite.aVec))} `
                                + `   A ${vdelta} ${vdelta} 0 1 0 ${s(sub(composite.a, composite.aVec))}`
                                + `   L ${s(sub(composite.b, composite.aVec))}`
                                + `   A ${vdelta} ${vdelta} 0 1 0 ${s(add(composite.b, composite.aVec))}`
                                // + `   M ${s(sub(composite.a, composite.aVec))}`
                                // + `   A ${vdelta} ${vdelta} 0 1 0 ${s(add(composite.a, composite.aVec))}`
                            } className="composite-soup-area" />
                            {
                                Array.from(clientStates.values()).filter((v: boolean | clientStatesT | Obj): v is clientStatesT => v instanceof Array).length &&
                                <path className="composite-soup-line" d={vecsToPath(composite.a, composite.b)} />
                            }
                            {Array.from(clientStates.values()).filter((v: boolean | clientStatesT | Obj): v is clientStatesT => v instanceof Array).map(([{ id }, vec, pt, inCircle]) =>
                            (<g key={id}>
                                <path className="composite-soup-perp" d={vecsToPath(pt, vec)} />
                                {inCircle || <CenteredImage pos={pt} img={Soup} scale={props.object_scale * 0.8} style={{ opacity: 0.7, filter: 'grayscale(0.7)' }} />}
                            </g>)
                            )}
                        </g>
                        :
                        <g>
                            <g className="connexions">{connexions.map(([a, b]) =>
                                <path className={[a.className, b.className].filter(x => x).join(' ')} key={`${a.id}-${b.id}`} d={vecsToPath(proj(a.vec), proj(b.vec))} />)
                            }</g>
                            <g className="soup-areas">{show_soup_areas && soups.map(([{ vec, id }]) => tup2(proj(vec), id)).map(([vec, id]) => <circle key={id} cx={vec[0]} cy={vec[1]} r={vdelta} />)}</g>
                        </g>
                }
            </g>
            <g>
                {sorted.map(([obj, set]) => {
                    let { movable, vec, image, alt_image, className, id, kind, displayArrows } = obj;
                    let pos = proj(vec);
                    let corresponding_soup = clientStates.get(id);
                    let deg = 0;
                    let soup_pos: Vec2 | undefined = isObj(corresponding_soup) ? proj(corresponding_soup.vec) : corresponding_soup instanceof Array ? corresponding_soup[2] : undefined;
                    if (soup_pos && props.near_clients_rotate) {
                        let rad = Math.atan2(soup_pos[1] - pos[1], soup_pos[0] - pos[0]);
                        if (rad > 0)
                            rad = rad - Math.PI
                        deg = (180 * rad) / Math.PI + 90;
                    }
                    return (
                        <g key={id} className={`object ${kind} ${className || ''}`} data-movable={movable}>
                            {displayArrows[0] && <Arrow className="X" marker="arrowhead-X" a={proj(vec)} b={f.X} hideArrow={vec[0] > 0.80} textAngle={-90} text={Math.round(vec[0] * 100) + '%'} />}
                            {displayArrows[1] && <Arrow className="Y" marker="arrowhead-Y" a={proj(vec)} b={f.Y} hideArrow={vec[1] > 0.70} dR={0.12} textAngle={0} text={Math.round(vec[1] * 100) + '%'} />}
                            {displayArrows[2] && <Arrow className="Z" marker="arrowhead-Z" a={proj(vec)} b={f.Z} hideArrow={vec[2] > 0.70} dR={0.12} textAngle={180} text={Math.round(vec[2] * 100) + '%'} />}
                            < g onPointerDown={(e: React.PointerEvent) => {
                                e.stopPropagation();
                                (e.target as any).setPointerCapture(e.pointerId);
                                if (!movable)
                                    return;
                                let initialMouse = getCoord(e);
                                setActive({ obj: [obj, set], initialMouse, initialVec: proj(obj.vec) });
                                setCurrentMouse(initialMouse);
                            }}>
                                <CenteredImage pos={pos} img={corresponding_soup ? image : alt_image || image}
                                    scale={props.object_scale} rotate={deg} />
                            </g>
                        </g>
                    );
                })}
            </g>
        </svg>
    </>);
};

